import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '@/lib/axios';
import { Button } from "@/components/ui/button";
import { useLoader } from "@/contexts/LoaderContext";
import { toast } from "sonner";
import { API_BASE_URL } from '@/config';
import { ShieldAlert, CheckCircle2 } from 'lucide-react';
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import JourneyHeader from "@/components/JourneyHeader";

const LenderErrorSimulation: React.FC = () => {
    const navigate = useNavigate();
    const { setLoading } = useLoader();
    const [errorCode, setErrorCode] = useState<string>("");
    const [customCode, setCustomCode] = useState<string>("");

    const handleSubmit = async () => {
        const codeToSend = errorCode === "custom" ? customCode : errorCode;

        if (!codeToSend) {
            toast.error("Please select or enter an error code.");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/process_lender_response/`, {
                error_code: codeToSend
            });

            if (response.data.success) {
                if (response.data.redirect_url) {
                    navigate(response.data.redirect_url);
                } else {
                    navigate('/final-offer'); // Fallback
                }
            } else {
                toast.error(response.data.message || "Failed to process response.");
            }
        } catch (err) {
            console.error("Failed to process lender response", err);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <JourneyHeader />

            <main className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                <div className="max-w-md w-full space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div className="bg-amber-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                        <ShieldAlert className="w-10 h-10 text-amber-600" />
                    </div>

                    <div className="space-y-4">
                        <h1 className="text-3xl font-bold text-gray-900 font-outfit">Simulate Lender Response</h1>
                        <p className="text-lg text-gray-600 leading-relaxed">
                            Select an error code to simulate the external API response.
                        </p>
                    </div>

                    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 my-8 text-left">
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="error-code">Error Code</Label>
                                <Select onValueChange={setErrorCode} value={errorCode}>
                                    <SelectTrigger id="error-code" className="bg-white">
                                        <SelectValue placeholder="Select a code" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="100">100 - Reject Current Lender</SelectItem>
                                        <SelectItem value="200">200 - Success Code</SelectItem>
                                        <SelectItem value="300">300 - Reject Current Offer</SelectItem>
                                        <SelectItem value="400">400 - Reject All Lenders</SelectItem>
                                        <SelectItem value="custom">Custom Code</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {errorCode === "custom" && (
                                <div className="space-y-2">
                                    <Label htmlFor="custom-code">Enter Custom Code</Label>
                                    <Input
                                        id="custom-code"
                                        placeholder="Enter code manually"
                                        value={customCode}
                                        onChange={(e) => setCustomCode(e.target.value)}
                                        className="bg-white"
                                    />
                                </div>
                            )}

                            <Button
                                onClick={handleSubmit}
                                className="w-full bg-intellend-green hover:bg-intellend-green/90 text-white h-12 text-lg font-semibold rounded-xl shadow-md mt-6"
                            >
                                Process Response
                            </Button>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default LenderErrorSimulation;
